import { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import AccountMemberPicker from "global/components/AccountMemberPicker/AccountMemberPicker";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { transferOwnership } from "screens/MySignRequests/DocumentSharingServices";

interface Props {
  /** The user whose documents are being handed off (null = dialog closed). */
  fromUser: { name?: string; email: string } | null;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Offboarding custody transfer — reassign every sign request owned by a departing user to another
 * account user. Admin-only (the backend enforces it too). The new-owner field is the shared
 * AccountMemberPicker, so you can only hand off to a real account user.
 */
const ReassignDocumentsDialog = ({ fromUser, onClose, onDone }: Props) => {
  const isOpen = Boolean(fromUser);
  const [newOwner, setNewOwner] = useState("");
  const [newOwnerValid, setNewOwnerValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewOwner("");
      setNewOwnerValid(false);
    }
  }, [isOpen]);

  const submit = async () => {
    const to = newOwner.trim();
    if (!fromUser?.email || !to) return;
    if (to.toLowerCase() === fromUser.email.toLowerCase()) {
      openErrorNotification("Choose a different user to reassign to.");
      return;
    }
    try {
      setSubmitting(true);
      const count = await transferOwnership(fromUser.email, to);
      openSuccessNotification(
        `Reassigned ${count} document${count === 1 ? "" : "s"} to ${to}.`,
      );
      onDone();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={isOpen}
      onClose={onClose}
      title="Reassign documents"
      size="sm"
      primaryLabel={submitting ? "Reassigning..." : "Reassign"}
      onPrimary={submit}
      // Custody can only be handed to a real, searched account member (validity comes from
      // AccountMemberPicker) — never an arbitrary/typed address that would orphan the
      // documents or transfer them outside the account.
      primaryDisabled={submitting || !newOwnerValid}
      primaryLoading={submitting}
      cancelLabel="Cancel"
      onCancel={onClose}
    >
      <Stack spacing={2} sx={{ minWidth: 360, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Move every sign request owned by{" "}
          <strong>{fromUser?.name || fromUser?.email}</strong> to another account
          user. Typically done when someone leaves the company.
        </Typography>
        <AccountMemberPicker
          id="reassign-new-owner"
          label="New owner"
          value={newOwner}
          onChange={setNewOwner}
          onValidityChange={setNewOwnerValid}
        />
      </Stack>
    </AppModal>
  );
};

export default ReassignDocumentsDialog;
